import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { array, boolean, mixed, number, object, string } from 'yup';
import { emptyHypotheses, initialState, uid } from './data';
import { calculate } from './mocks';
import type { CalculationInput, DevelopmentPlan, Hypotheses, Project, StudioState } from './types';

const KEY = 'plan-scenario-plans-v1';
const position = array().of(number().required()).length(2).required();
const rule = object({ mode: mixed().oneOf(['weighted', 'forbidden', 'forced']).required(), weight: number().positive().required() });
const hypotheses = object({
  categories: object().required().test('rules', 'Règles invalides', value => Object.values(value).every(v => rule.isValidSync(v))),
  local: object().required().test('rules', 'Exceptions invalides', value => Object.values(value).every(v => rule.isValidSync(v))),
  departures: array().of(object({
    id: string().required(), label: string().required(), roadId: string().required(),
    kind: mixed().oneOf(['network', 'plant']).required(), coordinates: array().of(position).min(2).required(),
  })).required(),
});
const stateSchema = object({
  project: object({
    name: string().required(), perimeter: string().required(), extent: string().required(), station: string().required(),
    dju: number().positive().required(), heatRatio: number().positive().required(), coldRatio: number().positive().required(),
    networkKind: mixed().oneOf(['existing', 'new']).required(), revision: number().required(),
  }).required(),
  scenarios: array().of(object({ id: string().required(), name: string().required(), hypotheses: hypotheses.required() })).required(),
  plans: array().of(object({
    id: string().required(), scenarioId: string().required(), name: string().required(), energy: mixed().oneOf(['heat', 'cold']).required(),
    subscribers: object().required().test('dates', 'Dates invalides', value => Object.values(value).every(v => typeof v === 'string')),
    pipes: array().of(object({
      id: string().required(), name: string().required(), manual: boolean().required(),
      coordinates: array().of(position).min(2).required(), month: string().defined(),
    })).required(), snapshot: hypotheses.optional().default(undefined), stale: boolean().required(),
  })).required(),
});

function load(): { state: StudioState; error: string } {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { state: initialState(), error: '' };
    const candidate: unknown = JSON.parse(raw);
    if (!stateSchema.isValidSync(candidate, { strict: true })) throw new Error('Format de sauvegarde invalide');
    // Schema validation above checks every field consumed by the prototype.
    return { state: candidate as StudioState, error: '' };
  } catch (error) {
    return { state: initialState(), error: `Sauvegarde locale illisible : ${error instanceof Error ? error.message : String(error)}. Réinitialisez explicitement pour enregistrer à nouveau.` };
  }
}
export const sameHypotheses = (a: Hypotheses, b: Hypotheses) => JSON.stringify(a) === JSON.stringify(b);

export function useStudio() {
  const [loaded] = useState(load);
  const [state, setState] = useState(loaded.state);
  const [storageError, setStorageError] = useState(loaded.error);
  useEffect(() => {
    if (storageError) return;
    try { localStorage.setItem(KEY, JSON.stringify(state)); }
    catch (error) { setStorageError(`Enregistrement local impossible : ${error instanceof Error ? error.message : String(error)}`); }
  }, [state, storageError]);

  const updatePlan = (id: string, patch: Partial<DevelopmentPlan>) => {
    setState(s => ({ ...s, plans: s.plans.map(p => p.id === id ? { ...p, ...patch } : p) }));
  };
  const updateHypotheses = (id: string, h: Hypotheses) => {
    setState(s => ({ ...s, scenarios: s.scenarios.map(c => c.id === id ? { ...c, hypotheses: h } : c) }));
  };
  const saveProject = (project: Project) => setState(s => {
    const invalidate = project.station !== s.project.station || project.dju !== s.project.dju ||
      project.heatRatio !== s.project.heatRatio || project.coldRatio !== s.project.coldRatio;
    return {
      ...s, project: { ...project, revision: s.project.revision + (invalidate ? 1 : 0) },
      plans: s.plans.map(p => ({ ...p, stale: p.stale || ((!!p.snapshot || p.pipes.length > 0) && invalidate) })),
    };
  });
  const addScenario = () => {
    const id = uid();
    setState(s => ({ ...s, scenarios: [...s.scenarios, {
      id, name: `Scénario ${s.scenarios.length + 1}`, hypotheses: emptyHypotheses(),
    }] }));
    return id;
  };
  const addPlan = (scenarioId: string) => {
    const id = uid();
    setState(s => ({ ...s, plans: [...s.plans, {
      id, scenarioId, name: `Plan de développement ${s.plans.filter(p => p.scenarioId === scenarioId).length + 1}`,
      energy: 'heat', subscribers: {}, pipes: [], stale: false,
    }] }));
    return id;
  };
  const mutation = useMutation({
    mutationFn: calculate,
    onSuccess: (pipes, input: CalculationInput) => {
      setState(s => ({ ...s, plans: s.plans.map(p => p.id === input.plan.id ? {
        ...p, pipes, snapshot: structuredClone(input.hypotheses), stale: false, projectRevision: s.project.revision,
      } : p) }));
    },
  });
  const reset = () => {
    try { localStorage.removeItem(KEY); setStorageError(''); setState(initialState()); }
    catch (error) { setStorageError(`Réinitialisation impossible : ${String(error)}`); }
  };
  return { state, setState, updatePlan, updateHypotheses, saveProject, addScenario, addPlan, mutation, storageError, reset };
}
export type Studio = ReturnType<typeof useStudio>;
